import { useMemo } from 'react';

import { useIntl } from 'react-intl';
import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';

import { Drawer, Tag } from 'antd';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import ActionButton from '@src/components/Buttons/ActionButton';
import FilterContainer from '@src/components/FilterContainer';
import MemberAvatar from '@src/components/MemberList/MemberAvatar';
import { ResponsiveTable } from '@src/components/Table';
import type { ResponsiveTableColumns } from '@src/components/Table';
import { getFullUrl } from '@src/packages/utils';
import { ALLOWED_ROLES } from '@src/packages/utils/access';
import { DateUtil } from '@src/packages/utils/dates';
import { resolveUserDisplayName } from '@src/packages/utils/user';
import { PATHS } from '@src/packages/workJourney/constants';
import IntlMessages from '@src/util/IntlMessages';

import { formatCoverDrawerTitle } from '../formatCoverDrawerTitle';
import type {
  IWorkJourneyCoverDrawerLocationState,
  IWorkJourneyCoverDrawerParams,
  IWorkJourneyRejectedDrawerUser,
} from '../types';
import { WorkJourneyCoverConflictAlert } from '../WorkJourneyCoverConflictAlert';
import { WorkJourneyRejectedChangeNotifiedModal } from './components/WorkJourneyRejectedChangeNotifiedModal';
import { useWorkJourneyResolveRejectedChange } from './hooks/useWorkJourneyResolveRejectedChange';
import styles from './styles.module.less';

export const WorkJourneyResolveRejectedChangeDrawer = () => {
  const intl = useIntl();
  const history = useHistory();
  const { url } = useRouteMatch();
  const { workJourneyConfigId, date } = useParams<IWorkJourneyCoverDrawerParams>();
  const location = useLocation<IWorkJourneyCoverDrawerLocationState | undefined>();
  const { shadow, journeyName, rejectedUsers: initialRejectedUsers = [], planning } =
    location.state ?? {};

  const title = useMemo(
    () => formatCoverDrawerTitle(journeyName, date, intl),
    [journeyName, date, intl],
  );

  const handleClose = () => {
    if (shadow) {
      history.goBack();
      return;
    }
    history.push(getFullUrl(PATHS.WORK_JOURNEY_MANAGEMENT, url));
  };

  const {
    rejectedUsers,
    busyEventId,
    notifiedOpen,
    handleResend,
    handleIgnore,
    closeNotifiedModal,
  } = useWorkJourneyResolveRejectedChange({
    initialRejectedUsers,
    onIgnoredAll: handleClose,
  });

  const handleNotifiedBackToStart = () => {
    closeNotifiedModal();
    handleClose();
  };

  const formatPlanningDate = (value?: string) => {
    if (!value) return '—';
    return (
      DateUtil.formatDateWithIntl(value, intl, {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      }) || value
    );
  };

  const planningRows = [
    {
      labelId: 'workJourney.management.resolve.planning.effectiveFrom',
      value: formatPlanningDate(planning?.effectiveFrom ?? date),
    },
    {
      labelId: 'workJourney.management.resolve.planning.journey',
      value: planning?.journeyName ?? journeyName ?? '—',
    },
    {
      labelId: 'workJourney.management.resolve.planning.shift',
      value: planning?.shiftDescription ?? '—',
    },
    {
      labelId: 'workJourney.management.resolve.planning.location',
      value: planning?.location ?? '—',
    },
  ];

  const columns: ResponsiveTableColumns<IWorkJourneyRejectedDrawerUser> = [
    {
      title: intl.formatMessage({ id: 'workJourney.management.cover.columns.user' }),
      key: 'user',
      render: (user: IWorkJourneyRejectedDrawerUser) => (
        <div className={styles.userCell}>
          <MemberAvatar size={32} user={user} />
          <span>{resolveUserDisplayName(user, user.id)}</span>
        </div>
      ),
    },
    {
      title: intl.formatMessage({ id: 'workJourney.management.cover.columns.idCard' }),
      dataIndex: 'idCard',
      key: 'idCard',
      // ResponsiveTable always passes the full row to `render` (not dataIndex value).
      render: (user: IWorkJourneyRejectedDrawerUser) => user.idCard || '—',
    },
    {
      title: intl.formatMessage({ id: 'workJourney.management.cover.columns.status' }),
      key: 'status',
      render: () => (
        <Tag color="purple" className="gx-mb-0">
          <IntlMessages id="workJourney.management.resolve.status" />
        </Tag>
      ),
    },
    {
      title: '',
      key: 'action',
      align: 'right',
      columnType: 'actions',
      actionsLayout: 'end',
      render: (user: IWorkJourneyRejectedDrawerUser) => {
        const rowBusy = busyEventId === user.eventId;
        return (
          <ActionButton.Container
            buttons={[
              <ActionButton
                key="resend"
                label={intl.formatMessage({ id: 'workJourney.management.resolve.resend' })}
                onClick={() => handleResend(user)}
                type="primary"
                color="primary"
                size="medium"
                loading={rowBusy}
                disabled={Boolean(busyEventId) && !rowBusy}
                allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE}
                disabledScaling
              />,
              <ActionButton
                key="ignore"
                label={intl.formatMessage({ id: 'workJourney.management.resolve.ignore' })}
                onClick={() => handleIgnore(user)}
                type="default"
                color="secondary"
                size="medium"
                disabled={Boolean(busyEventId)}
                allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE}
                disabledScaling
              />,
            ]}
          />
        );
      },
    },
  ];

  return (
    <Drawer
      open={Boolean(workJourneyConfigId && date)}
      width="100%"
      title={null}
      closable={false}
      footer={null}
      destroyOnClose
      onClose={handleClose}
    >
      <BoxContainer>
        <BoxContainer content fixed bottomBorder>
          <FilterContainer goBack={handleClose} title={<Title.Header value={title} />} />
        </BoxContainer>

        <BoxContainer fullContent>
          <Title.SubTitle
            className="gx-mb-3"
            value={<IntlMessages id="workJourney.management.cover.conflict" />}
          />

          <WorkJourneyCoverConflictAlert
            variant="info"
            title={<IntlMessages id="workJourney.management.resolve.alertTitle" />}
            description={<IntlMessages id="workJourney.management.resolve.alertDescription" />}
          />

          <Title.SubTitle
            className="gx-mt-4 gx-mb-3"
            value={<IntlMessages id="workJourney.management.resolve.planningSection" />}
          />

          <dl className={styles.planningList}>
            {planningRows.map(row => (
              <div key={row.labelId} className={styles.planningRow}>
                <dt>
                  <IntlMessages id={row.labelId} />
                </dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>

          <Title.SubTitle
            className="gx-mt-4 gx-mb-3"
            value={<IntlMessages id="workJourney.management.resolve.usersSection" />}
          />

          <ResponsiveTable dataSource={rejectedUsers} columns={columns} />
        </BoxContainer>
      </BoxContainer>

      <WorkJourneyRejectedChangeNotifiedModal
        open={notifiedOpen}
        onBackToStart={handleNotifiedBackToStart}
      />
    </Drawer>
  );
};
